import {Doctor, FormValues} from '@/type';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import axios from 'axios';
import {Controller, useForm} from 'react-hook-form';
import DoctorsSelectMobile from '../doctors/DoctorsSelectMobile';
import DoctorsSelect from '../doctors/DoctorsSelect';
import {API_ROUTE, HOST} from '../../config';

import styles from './PostponementTeleporada.module.scss';
import AddressOnline from '../addressOnline/AddressOnline';
import CalendarDesktopViewOnline from '../calendarOnline/CalendarDesktopViewOnline';
import CalendarMobileViewOnline, {
  CalendarApiService
} from '../calendarOnline/CalendarMobileViewOnline';

export default function PostponementTeleporada() {
  const params = new URLSearchParams(window.location.search);
  const orderId = params.get('order_id');
  const hash = params.get('hash');

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: {isSubmitting}
  } = useForm<FormValues>({
    defaultValues: {
      selected_doctor: {id: '', name: ''},
      selected_date: new Date(),
      selected_time: {time: '', address: '', id: '', desc: ''}
    }
  });

  const [productId, setProductId] = useState<number>();
  const [addressOption, setAddressOption] = useState<any[]>([]);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [schedule, setSchedule] = useState<CalendarApiService[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const hasFetchedInit = useRef(false);
  const hasFetchedDoctors = useRef(false);
  const hasSetInitialDate = useRef(false);
  const [eligibility, setEligibility] = useState<{
    code: number;
    message: string;
  } | null>(null);

  const [slotError, setSlotError] = useState<string | null>(null);
  const [initialDoctor, setInitialDoctor] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const selectedDoctor = watch('selected_doctor');
  const selectedDate = watch('selected_date');
  const selectedTime = watch('selected_time');

  const availableDoctorIds = useMemo(() => {
    try {
      if (!Array.isArray(schedule)) {
        return new Set<string>();
      }
      const ids = new Set<string>();
      for (const day of schedule) {
        if (!day?.Rows) continue;
        for (const row of day.Rows) {
          ids.add(String(row.DoctorId));
        }
      }
      return ids;
    } catch (e) {
      return new Set<string>();
    }
  }, [schedule]);

  const formatDate = (date: Date): string => {
    return date.toISOString().split('T')[0];
  };

  function getSchedule(pid?: number) {
    const p = pid || productId;
    if (!p) return;
    const today = new Date();
    const endDate = new Date();
    endDate.setDate(today.getDate() + 39);
    const startDateStr = formatDate(today);
    const endDateStr = formatDate(endDate);

    const doctorParam =
      selectedDoctor?.name &&
      selectedDoctor.name !== 'Dowolny lekarz' &&
      selectedDoctor.id
        ? `&doctorID=${encodeURIComponent(String(selectedDoctor.id))}`
        : '';
    axios
      .get<{status: string; data: CalendarApiService[]}>(
        `${API_ROUTE}/get_list_schedule_teleporada_and_zasvichenie?date_start=${startDateStr}&date_end=${endDateStr}&id=${p}${doctorParam}`
      )
      .then((res) => {
        setSchedule(res.data.data);
      })
      .catch((err) => {
        console.error('Error fetching schedule:', err);
      });
  }

  useEffect(() => {
    // Init data from backend using order_id + hash
    if (hasFetchedInit.current) return;
    hasFetchedInit.current = true;
    if (!orderId || !hash) {
      setIsLoading(false);
      return;
    }

    axios
      .get(`${API_ROUTE}/postponement_init`, {
        params: {order_id: orderId, hash}
      })
      .then((res) => {
        const data = res.data;
        if (data?.product_id) setProductId(parseInt(data.product_id, 10));
        if (Array.isArray(data?.address_options))
          setAddressOption(data.address_options);
        if (data?.eligibility) setEligibility(data.eligibility);

        if (data?.doctor) {
          const docId = String(data.doctor.id || '');
          const docName = String(data.doctor.name || 'Dowolny lekarz');
          const doctor = {id: docId, name: docName};
          const defaultDoctor = {id: '', name: 'Dowolny lekarz'};
          setValue('selected_doctor', defaultDoctor);
          setInitialDoctor(doctor);
        } else {
          const defaultDoctor = {id: '', name: 'Dowolny lekarz'};
          setValue('selected_doctor', defaultDoctor);
          setInitialDoctor(defaultDoctor);
        }
      })
      .catch((err) => {
        console.error('Init error:', err);
        setEligibility({code: 4, message: 'Ten porządek nie istnieje.'});
      })
      .finally(() => setIsLoading(false));
  }, [orderId, hash, setValue]);

  useEffect(() => {
    // Fetch doctors once
    if (hasFetchedDoctors.current) return;
    hasFetchedDoctors.current = true;
    axios
      .get(`${API_ROUTE}/get_doctor`, {
        withCredentials: true,
        headers: {'Content-Type': 'application/json'}
      })
      .then((res) => setDoctors(res.data.data_doctors))
      .catch((err) => console.error('Error fetching doctors', err));
  }, []);

  useEffect(() => {
    if (productId) {
      getSchedule(productId);
    }
  }, [productId]);

  useEffect(() => {
    if (
      schedule &&
      Array.isArray(schedule) &&
      schedule.length > 0 &&
      !hasSetInitialDate.current // Only once
    ) {
      try {
        const now = new Date();
        const availableDates = schedule
          .filter((item) => {
            const itemDate = new Date(item.Date);
            const isToday =
              itemDate.getFullYear() === now.getFullYear() &&
              itemDate.getMonth() === now.getMonth() &&
              itemDate.getDate() === now.getDate();

            if (!item.Rows) return false;

            return item.Rows.some((row) => {
              const doctorMatch =
                !selectedDoctor.id ||
                selectedDoctor.id === '' ||
                String(row.DoctorId) === selectedDoctor.id;

              const time = new Date(row.Time);
              const timeValid = !isToday || time > now;

              return doctorMatch && timeValid;
            });
          })
          .map((item) => item.Date)
          .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

        if (availableDates.length > 0) {
          const firstAvailableDate = new Date(availableDates[0]);
          hasSetInitialDate.current = true;
          setValue('selected_date', firstAvailableDate);
        }
      } catch (error) {
        console.error('Error in auto-select date:', error);
      }
    }
  }, [schedule, selectedDoctor, setValue]);

  useEffect(() => {
    // Reset selected time when date or doctor changes
    setValue('selected_time', {
      time: '',
      address: '',
      id: '',
      idsSameName: '',
      officeSameNameDoctors: '',
      desc: ''
    });
  }, [selectedDate, selectedDoctor, setValue]);

  const onSubmit = async (data: any) => {
    try {
      if (!orderId || !hash) return;
      if (eligibility && eligibility.code !== 1) return;

      let selectedDateFormatted;
      if (data.selected_date instanceof Date) {
        const year = data.selected_date.getFullYear();
        const month = String(data.selected_date.getMonth() + 1).padStart(2, '0');
        const day = String(data.selected_date.getDate()).padStart(2, '0');
        selectedDateFormatted = `${year}-${month}-${day}`;
      } else {
        selectedDateFormatted = data.selected_date;
      }

      const payload = {
        order_id: orderId,
        hash,
        postponement_date: selectedDateFormatted,
        postponement_time: data.selected_time.time,
        postponement_office: data.selected_time.id,
        selected_cabinet_name: data.selected_time.address
      };

      const headers: any = {'Content-Type': 'application/json'};
      const wpNonce = (window as any).postponementFormVars?.nonce;
      if (wpNonce) headers['X-WP-Nonce'] = wpNonce;

      const response = await axios.post(
        `${API_ROUTE}/postponement_update`,
        payload,
        {headers}
      );
      if (response?.data?.success) {
        window.location.href = `${HOST}/postponement-thanks/?order_id=${orderId}&status=success`;
      } else {
        window.location.href = `${HOST}/postponement-thanks/?order_id=${orderId}&status=notsuccess`;
      }
    } catch (err) {
      console.error('Postponement error:', err);
      window.location.href = `${HOST}/postponement-thanks/?order_id=${orderId}&status=notsuccess`;
    }
  };

  return (
    <div>
      {isLoading && (
        <div
          className='loading-indicator'
          style={{marginBottom: 16, marginTop: '10%'}}
        >
          <div className='spinner'></div>
          <p>Ładowanie danych…</p>
        </div>
      )}

      {!isLoading && eligibility && eligibility.code !== 1 && (
        <div className={styles.container}>
          <div className={styles.errorMessages}>
            {eligibility.message || 'Zmiana terminu niedozwolona.'}
          </div>
        </div>
      )}

      {!isLoading && (!eligibility || eligibility.code === 1) && (
        <div className={styles.container}>
          <h1 className={styles.postponementTitle}>
            <span>Zmiana</span> terminu wizyty
          </h1>
          <p className={styles.postponementSubtitle}>
            Jeśli chcesz zmienić termin wizyty, skorzystaj z poniższego
            formularza
          </p>
          <div className={styles.postponementWarning}>
            <span>UWAGA!</span> Tę wizytę można przełożyć tylko raz. Po zmianie
            terminu otrzymają Państwo potwierdzenie e-mail. Każda kolejna
            rejestracja również będzie miała możliwość jednorazowego przełożenia
            wizyty.
          </div>
          <form
            className='r-form-single-product'
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className='step-container'>
              <div className='r-form-fields-container'>
                {initialDoctor && initialDoctor.name !== 'Dowolny lekarz' && (
                  <Controller
                    name='selected_doctor'
                    control={control}
                    render={({field}) => (
                      <>
                        <div className='doctor-mobile'>
                          <DoctorsSelectMobile
                            value={field.value}
                            onChange={field.onChange}
                            doctors={doctors}
                            availableDoctorIds={availableDoctorIds}
                          />
                        </div>
                        <div className='doctor-desktop'>
                          <DoctorsSelect
                            value={field.value}
                            onChange={field.onChange}
                            doctors={doctors}
                            availableDoctorIds={availableDoctorIds}
                          />
                        </div>
                      </>
                    )}
                  />
                )}

                <Controller
                  name='selected_date'
                  control={control}
                  render={({field}) => (
                    <>
                      <div className='calendar-mobile'>
                        <CalendarMobileViewOnline
                          {...field}
                          productId={productId}
                          doctorId={selectedDoctor?.id}
                          schedule={schedule}
                        />
                      </div>
                      <div className='calendar-desktop'>
                        <CalendarDesktopViewOnline
                          {...field}
                          productId={productId}
                          doctorId={selectedDoctor?.id}
                          schedule={schedule}
                        />
                      </div>
                    </>
                  )}
                />

                <Controller
                  name='selected_time'
                  control={control}
                  render={({field}) => {
                    let filteredSchedule = schedule;
                    if (Array.isArray(schedule)) {
                      filteredSchedule = schedule.filter((item) => {
                        return item.Rows.some((row) => {
                          const doctorMatch =
                            !selectedDoctor?.id ||
                            selectedDoctor.id === '' ||
                            String(row.DoctorId) ===
                              String(selectedDoctor.id);
                          return doctorMatch;
                        });
                      });
                    }

                    return (
                      <>
                        <AddressOnline
                          {...field}
                          productId={productId}
                          doctorId={selectedDoctor?.id}
                          addressOption={addressOption}
                          schedule={filteredSchedule}
                          selectedDate={selectedDate}
                          doctors={doctors}
                          onChange={field.onChange}
                        />
                        {slotError && (
                          <div
                            style={{
                              color: 'red',
                              marginTop: 8,
                              fontSize: 14
                            }}
                          >
                            {slotError}
                          </div>
                        )}
                      </>
                    );
                  }}
                />

                <button
                  type='submit'
                  className={styles['submit-button']}
                  disabled={
                    !selectedTime?.time ||
                    isSubmitting ||
                    Boolean(eligibility && eligibility.code !== 1)
                  }
                >
                  Zatwierdź nowy termin
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
